import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useAllAdminOrganizations } from '@/hooks/useAllAdminOrganizations';

interface OrgLookupComboboxProps {
  /** Currently selected partner organization id, or null when none. */
  value?: string | null;
  /** Called with the chosen partner id, or null when "None" is selected. */
  onSelect: (organizationId: string | null) => void;
  /** Organization id to exclude from the options (e.g. the org being edited). */
  excludeId?: string;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Searchable picker over partner organizations (IsPartner = true), backed by
 * the existing `?isPartner=true` list filter. Used to choose Source/Current
 * partner associations.
 */
export function OrgLookupCombobox({
  value,
  onSelect,
  excludeId,
  disabled,
  placeholder = 'Select partner…',
}: OrgLookupComboboxProps) {
  const [open, setOpen] = useState(false);
  const { data: partners, isLoading } = useAllAdminOrganizations({ isPartner: true });

  const options = useMemo(
    () => (partners ?? []).filter((org) => org.id !== excludeId),
    [partners, excludeId]
  );

  // Resolve the selected partner's display name; fall back to the raw id.
  const selectedName = useMemo(() => {
    if (!value) return undefined;
    return options.find((org) => org.id === value)?.name ?? value;
  }, [options, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', !selectedName && 'text-muted-foreground')}>
            {selectedName ?? placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search partners…" />
          <CommandList>
            <CommandEmpty>
              {isLoading ? 'Loading partners…' : 'No partner organizations found.'}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__"
                onSelect={() => {
                  onSelect(null);
                  setOpen(false);
                }}
              >
                <Check className={cn('mr-2 h-4 w-4', value ? 'opacity-0' : 'opacity-100')} />
                None
              </CommandItem>
              {options.map((org) => (
                <CommandItem
                  key={org.id}
                  value={`${org.name} ${org.id}`}
                  onSelect={() => {
                    onSelect(org.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn('mr-2 h-4 w-4', value === org.id ? 'opacity-100' : 'opacity-0')}
                  />
                  <span className="truncate">{org.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
